import { BcmUserPermission } from '@modules/bcm/bcm-user-permission';
import { BcmTenantPermission } from '@modules/bcm/bcm-tenant-permission';
import { ListItemType, U2bNavigationItem } from './navigation.shared';
import { BcmVersions } from '@modules/bcm/bcm-versions';

export interface BcmNavigationItem extends U2bNavigationItem {
    permission?: string;
    permissions?: string[];
    requireAnyPermission?: boolean;
    tenantPermission?: BcmTenantPermission;
    tenantPermissions?: BcmTenantPermission[];
    requireAnyTenantPermission?: boolean;
    children?: BcmNavigationItem[];
    type: ListItemType;
    isText?: boolean;
    forceShowing?: boolean;
    notEditable?: boolean;
    availableFor?: BcmVersions[];
}

enum BcmNavigationElementKey {
    Dashboard = 'dashboard',
    Persons = 'persons',
    Companies = 'companies',
    Relations = 'relations',
    Products = 'products',
    ProductCategories = 'product_categories',
    Vouchers = 'vouchers',
    Boats = 'boats',
    Piers = 'piers',
    BerthsTable = 'berths_table',
    VisualBerthPlan = 'visual_berth_plan',
    BerthsScheduler = 'berths_scheduler',
    WinterStorages = 'winter_storages',
    DocksiteOrder = 'docksite_order',
    ElectricMeters = 'electric_meters',
    Keys = 'keys',
    ExcelReports = 'excel_reports',
    AssociationReports = 'association_reports',
    NewInvoice = 'new_invoice',
    Invoices = 'invoices',
    Quotes = 'quotes',
    InvoiceCollections = 'invoice_collections',
    InvoiceSettings = 'invoice_settings',
    InvoiceDefaultTemplates = 'invoice_default_templates',
    InvoiceWdTemplates = 'invoice_wd_templates',
    DATEV = 'datev_export',
    SEPA = 'sepa',
    POS = 'pos',
    NewMessage = 'new_message',
    SentMessage = 'sent_message',
    Users = 'users',
    Settings = 'settings',
    UserCustomSettings = 'user_custom_settings',
    RolesAndPermissions = 'roles_and_permissions',
    Contracts = 'contracts',
    ContractAttachments = 'contract_attachments',
    ContractsWd = 'contracts_wd',
    RecipientLists = 'recipient_lists',
    EmailTemplates = 'email_templates',
    EmailMailings = 'email_mailings',
    Seasons = 'seasons',
    ImportData = 'import',
    Documents = 'documents_list',
    DocumentCategories = 'document_categories',
    News = 'news',
    CostCenters = 'cost_centers',
    Absences = 'absences',
    Bookings = 'bookings',
    Departments = 'departments',
    Employees = 'employees',
    WorkingTimeManagement = 'working_time_management',
}

export const navigationElements: { [key in BcmNavigationElementKey]: BcmNavigationItem } = {
    [BcmNavigationElementKey.Dashboard]: {
        id: 'my_harbour',
        translate: 'nav.bcm.myHarbour',
        type: ListItemType.Item,
        icon: 'dashboard',
        url: 'dashboards'
    },
    [BcmNavigationElementKey.Persons]: {
        id: 'persons',
        translate: 'nav.bcm.persons',
        type: ListItemType.Item,
        url: 'persons',
        permission: BcmUserPermission.PERSONS_READ,
        tenantPermission: BcmTenantPermission.PERSONS
    },
    [BcmNavigationElementKey.Companies]: {
        id: 'companies',
        translate: 'nav.bcm.companies',
        type: ListItemType.Item,
        url: 'companies',
        permission: BcmUserPermission.COMPANIES_READ,
        tenantPermission: BcmTenantPermission.ORGANISATIONS
    },
    [BcmNavigationElementKey.Relations]: {
        id: 'relations',
        translate: 'nav.bcm.tenantRelations',
        type: ListItemType.Item,
        url: 'tenant-relations',
        permission: BcmUserPermission.TENANT_RELATIONS_READ,
        tenantPermission: BcmTenantPermission.TENANT_RELATIONS
    },
    [BcmNavigationElementKey.Products]: {
        id: 'products',
        translate: 'nav.bcm.products',
        type: ListItemType.Item,
        url: 'products',
        permission: BcmUserPermission.PRODUCTS_READ,
        tenantPermission: BcmTenantPermission.PRODUCTS
    },
    [BcmNavigationElementKey.ProductCategories]: {
        id: 'product_categories',
        translate: 'nav.bcm.productCategories',
        type: ListItemType.Item,
        url: 'product-categories',
        permission: BcmUserPermission.PRODUCT_CATEGORIES_READ,
        tenantPermission: BcmTenantPermission.PRODUCTS
    },
    [BcmNavigationElementKey.Vouchers]: {
        id: 'vouchers',
        translate: 'nav.bcm.vouchers',
        type: ListItemType.Item,
        url: 'vouchers',
        permission: BcmUserPermission.PRODUCTS_READ,
        tenantPermission: BcmTenantPermission.BERTH_VOUCHER,
    },
    [BcmNavigationElementKey.News]: {
        id: 'news',
        translate: 'nav.bcm.newsAndInfo',
        type: ListItemType.Item,
        url: 'news',
        permission: BcmUserPermission.NEWS_READ,
        tenantPermission: BcmTenantPermission.NEWS
    },
    [BcmNavigationElementKey.Boats]: {
        id: 'boats',
        translate: 'nav.bcm.boats',
        type: ListItemType.Item,
        url: 'boats',
        permission: BcmUserPermission.BOATS_READ,
        tenantPermission: BcmTenantPermission.BOATS
    },
    [BcmNavigationElementKey.Piers]: {
        id: 'piers',
        translate: 'nav.bcm.piers',
        type: ListItemType.Item,
        url: 'piers',
        permission: BcmUserPermission.BERTHS_READ,
        tenantPermission: BcmTenantPermission.BERTHS_PIERS,
    },
    [BcmNavigationElementKey.VisualBerthPlan]: {
        id: 'visualBerthPlan',
        translate: 'nav.bcm.visualBerthPlan',
        type: ListItemType.Item,
        url: 'berths/map',
        permission: BcmUserPermission.BERTHS_READ,
        tenantPermission: BcmTenantPermission.VISUAL_BERTH_PLAN
    },
    [BcmNavigationElementKey.BerthsTable]: {
        id: 'berths-table',
        translate: 'nav.bcm.berthsTable',
        type: ListItemType.Item,
        url: 'berths/list',
        permission: BcmUserPermission.BERTHS_READ,
        tenantPermission: BcmTenantPermission.BERTHS_TABLE
    },
    [BcmNavigationElementKey.BerthsScheduler]: {
        id: 'berths-scheduler',
        translate: 'nav.bcm.bookingCalendar',
        type: ListItemType.Item,
        url: 'berths/scheduler',
        permission: BcmUserPermission.BERTHS_READ,
        tenantPermission: BcmTenantPermission.BERTH_SCHEDULER
    },
    [BcmNavigationElementKey.WinterStorages]: {
        id: 'winter_storages',
        translate: 'nav.bcm.winterStorages',
        type: ListItemType.Item,
        url: 'winter-storages',
        permission: BcmUserPermission.WINTER_STORAGES_READ,
        tenantPermission: BcmTenantPermission.WINTER_STORAGES
    },
    [BcmNavigationElementKey.DocksiteOrder]: {
        id: 'docksite_order',
        translate: 'nav.bcm.docksiteOrder',
        type: ListItemType.Item,
        url: 'orders',
        permission: BcmUserPermission.ORDERS_READ,
        tenantPermission: BcmTenantPermission.DOCKSITE
    },
    // {
    //     id: 'Liegeplatz Manager',
    //     title: 'Liegeplatz Manager',
    //     type: ListItemType.Item,
    //     url: 'berth-scheduler',
    //     permission: BcmUserPermission.BERTHS_READ,
    //     tenantPermission: BcmTenantPermission.BERTH_SCHEDULER
    // },
    [BcmNavigationElementKey.ElectricMeters]: {
        id: 'electric_meters',
        translate: 'nav.bcm.electricMeters',
        type: ListItemType.Item,
        classes: 'premium-nav-link',
        url: 'electric-meters',
        permission: BcmUserPermission.ELECTRIC_METERS_READ,
        tenantPermission: BcmTenantPermission.ELECTRIC_METERS
    },
    [BcmNavigationElementKey.Keys]: {
        id: 'keys',
        translate: 'nav.bcm.keys',
        type: ListItemType.Item,
        classes: 'premium-nav-link',
        url: 'keys',
        permission: BcmUserPermission.KEYS_READ,
        tenantPermission: BcmTenantPermission.KEYS
    },
    [BcmNavigationElementKey.ExcelReports]: {
        id: 'excel_reports',
        translate: 'nav.bcm.excelReports',
        type: ListItemType.Item,
        url: 'reports/default',
        permission: BcmUserPermission.DOWNLOAD_REPORTS,
        tenantPermission: BcmTenantPermission.REPORTS
    },
    [BcmNavigationElementKey.AssociationReports]: {
        id: 'association_reports',
        translate: 'nav.bcm.associationReports',
        type: ListItemType.Item,
        url: 'reports/association',
        permission: BcmUserPermission.ASSOCIATION_REPORTS,
        tenantPermission: BcmTenantPermission.ASSOCIATION_REPORTS
    },
    [BcmNavigationElementKey.NewInvoice]: {
        id: 'new_invoice',
        translate: 'nav.bcm.newVoucher',
        type: ListItemType.Item,
        url: 'accounting/invoices/new',
        permission: BcmUserPermission.INVOICES_WRITE,
        tenantPermission: BcmTenantPermission.INVOICES,
        exactMatch: true
    },
    [BcmNavigationElementKey.Invoices]: {
        id: 'invoices',
        translate: 'nav.bcm.invoices',
        type: ListItemType.Item,
        url: 'accounting/invoices',
        permission: BcmUserPermission.INVOICES_READ,
        tenantPermission: BcmTenantPermission.INVOICES,
        exactMatch: true
    },
    [BcmNavigationElementKey.Quotes]: {
        id: 'quotes',
        translate: 'nav.bcm.quotes',
        type: ListItemType.Item,
        classes: 'premium-nav-link',
        url: 'accounting/quotes',
        permission: BcmUserPermission.QUOTES_READ,
        tenantPermission: BcmTenantPermission.QUOTES,
        exactMatch: true
    },
    [BcmNavigationElementKey.InvoiceCollections]: {
        id: 'invoice_collections',
        translate: 'nav.bcm.invoiceCollections',
        type: ListItemType.Item,
        classes: 'premium-nav-link',
        url: 'accounting/invoice-collections',
        permission: BcmUserPermission.INVOICE_COLLECTIONS_READ,
        tenantPermission: BcmTenantPermission.INVOICE_COLLECTION
    },
    // {
    //     id: 'Gutschriften',
    //     title: 'Gutschriften',
    //     type: ListItemType.Item,
    //     url: '#'
    // },
    // {
    //     id: 'Mahnungen',
    //     title: 'Mahnungen',
    //     type: ListItemType.Item,
    //     url: '#'
    // },
    // {
    //     id: 'Preislisten',
    //     title: 'Preislisten',
    //     type: ListItemType.Item,
    //     url: 'accounting-price-lists'
    // }
    [BcmNavigationElementKey.InvoiceSettings]: {
        id: 'invoice_settings',
        translate: 'nav.bcm.invoiceSettings',
        type: ListItemType.Item,
        url: 'accounting/invoice-settings',
        permission: BcmUserPermission.INVOICES_READ,
        tenantPermission: BcmTenantPermission.INVOICES
    },
    [BcmNavigationElementKey.InvoiceDefaultTemplates]: {
        id: 'invoice-default-templates',
        translate: 'nav.bcm.invoiceDefaultTemplates',
        type: ListItemType.Item,
        url: 'accounting/invoice-templates/default',
        permission: BcmUserPermission.INVOICE_WD_TEMPLATES_READ,
        tenantPermission: BcmTenantPermission.INVOICES
    },
    [BcmNavigationElementKey.InvoiceWdTemplates]: {
        id: 'invoice-wd-templates',
        translate: 'nav.bcm.invoiceWordTemplates',
        type: ListItemType.Item,
        url: 'accounting/invoice-templates/word',
        permission: BcmUserPermission.INVOICE_WD_TEMPLATES_READ,
        tenantPermission: BcmTenantPermission.RECEIPT_WORD_DESIGNER
    },
    [BcmNavigationElementKey.DATEV]: {
        id: 'datev_export',
        translate: 'nav.bcm.datevExport',
        type: ListItemType.Item,
        classes: 'premium-nav-link',
        url: 'accounting/invoices/datev-export',
        permissions: [
            BcmUserPermission.INVOICE_EXPORT_DATEV,
            BcmUserPermission.INVOICE_EXPORT_XDOC,
            BcmUserPermission.INVOICE_EXPORT_SYSKA,
        ],
        requireAnyPermission: true,
        tenantPermissions: [
            BcmTenantPermission.DATEV,
            BcmTenantPermission.XDOC_EXPORT,
            BcmTenantPermission.SYSKA_EXPORT,
        ],
        requireAnyTenantPermission: true,
    },
    [BcmNavigationElementKey.SEPA]: {
        id: 'sepa',
        translate: 'nav.bcm.sepa',
        type: ListItemType.Item,
        classes: 'premium-nav-link',
        url: 'accounting/sepa-runs',
        permission: BcmUserPermission.SEPA_RUN_READ,
        tenantPermission: BcmTenantPermission.SEPA
    },
    [BcmNavigationElementKey.POS]: {
        id: 'pos',
        translate: 'nav.bcm.pos',
        type: ListItemType.Item,
        classes: 'premium-nav-link',
        url: 'accounting/cash-registers',
        permission: BcmUserPermission.CASH_REGISTER_READ,
        tenantPermission: BcmTenantPermission.CASH
    },
    [BcmNavigationElementKey.NewMessage]: {
        id: 'new_message',
        translate: 'nav.bcm.newMessage',
        type: ListItemType.Item,
        url: 'messages/mails/new',
        permission: BcmUserPermission.MAIL_MESSAGES_WRITE,
        tenantPermission: BcmTenantPermission.MESSAGES_EMAIL,
        exactMatch: true
    },
    [BcmNavigationElementKey.SentMessage]: {
        id: 'sent_message',
        translate: 'nav.bcm.sentMessages',
        type: ListItemType.Item,
        url: 'messages/mails',
        permission: BcmUserPermission.MAIL_MESSAGES_READ,
        tenantPermission: BcmTenantPermission.MESSAGES_EMAIL,
        exactMatch: true
    },
    [BcmNavigationElementKey.Users]: {
        id: 'users',
        translate: 'nav.bcm.users',
        type: ListItemType.Item,
        icon: 'people',
        url: 'users',
        permission: BcmUserPermission.FULL_GRANT
    },
    [BcmNavigationElementKey.RolesAndPermissions]: {
        id: 'roles_and_permissions',
        translate: 'nav.bcm.rolesAndPermissions',
        type: ListItemType.Item,
        icon: 'security',
        url: 'roles-and-permissions',
        permission: BcmUserPermission.FULL_GRANT,
        tenantPermission: BcmTenantPermission.ROLES_PERMISSIONS
    },
    [BcmNavigationElementKey.ImportData]: {
        id: 'data-import',
        translate: 'nav.bcm.dataImport',
        type: ListItemType.Item,
        icon: 'input',
        url: 'import',
        permission: BcmUserPermission.FULL_GRANT,
        notEditable: true
    },
    [BcmNavigationElementKey.Contracts]: {
        id: 'contracts',
        translate: 'nav.bcm.contractTemplates',
        type: ListItemType.Item,
        url: 'contract-manager/wysiwyg',
        permission: BcmUserPermission.CONTRACTS_READ,
        tenantPermission: BcmTenantPermission.CONTRACT_MANAGER
    },
    [BcmNavigationElementKey.ContractsWd]: {
        id: 'contracts-wd',
        translate: 'nav.bcm.contractTemplatesWord',
        type: ListItemType.Item,
        url: 'contract-manager/word',
        permission: BcmUserPermission.CONTRACTS_READ,
        tenantPermission: BcmTenantPermission.CONTRACT_MANAGER
    },
    [BcmNavigationElementKey.ContractAttachments]: {
        id: 'contract_attachments',
        translate: 'nav.bcm.contractAttachments',
        type: ListItemType.Item,
        url: 'contract-manager/attachments',
        permission: BcmUserPermission.CONTRACTS_READ,
        tenantPermission: BcmTenantPermission.CONTRACT_MANAGER
    },
    [BcmNavigationElementKey.RecipientLists]: {
        id: 'recipient_lists',
        translate: 'nav.bcm.recipientLists',
        type: ListItemType.Item,
        url: 'messages/mails/recipient-lists',
        permission: BcmUserPermission.MAIL_MESSAGES_READ,
        tenantPermission: BcmTenantPermission.MESSAGES_EMAIL,
        exactMatch: true
    },
    [BcmNavigationElementKey.EmailTemplates]: {
        id: 'email_templates',
        translate: 'nav.bcm.emailTemplates',
        type: ListItemType.Item,
        url: 'email-templates',
        permission: BcmUserPermission.MAIL_TEMPLATES_READ,
        tenantPermission: BcmTenantPermission.MESSAGES_EMAIL
    },
    [BcmNavigationElementKey.EmailMailings]: {
        id: 'email_mailings',
        translate: 'nav.bcm.emailMailings',
        type: ListItemType.Item,
        url: 'email-mailings',
        permission: BcmUserPermission.MAIL_MAILINGS_READ,
        tenantPermission: BcmTenantPermission.MESSAGES_EMAIL
    },
    [BcmNavigationElementKey.Seasons]: {
        id: 'seasons',
        translate: 'nav.bcm.seasons',
        type: ListItemType.Item,
        classes: 'premium-nav-link',
        url: 'seasons',
        permission: BcmUserPermission.SEASONS_READ,
        tenantPermission: BcmTenantPermission.SEASONS
    },

    [BcmNavigationElementKey.Documents]: {
        id: 'documents_list',
        translate: 'nav.bcm.documents',
        type: ListItemType.Item,
        url: 'documents/list',
        permission: BcmUserPermission.DOCUMENTS_READ,
        tenantPermission: BcmTenantPermission.DOCUMENTS_MANAGEMENT
    },
    [BcmNavigationElementKey.DocumentCategories]: {
        id: 'documentCategories',
        translate: 'nav.bcm.documentCategories',
        type: ListItemType.Item,
        url: 'documents/categories',
        permission: BcmUserPermission.DOCUMENT_CATEGORIES_READ,
        tenantPermission: BcmTenantPermission.DOCUMENTS_MANAGEMENT
    },
    [BcmNavigationElementKey.Settings]: {
        id: 'settings',
        translate: 'nav.bcm.harbourSettings',
        type: ListItemType.Item,
        url: 'settings',
        permission: BcmUserPermission.FULL_GRANT,
        notEditable: true
    },
    [BcmNavigationElementKey.UserCustomSettings]: {
        id: 'user_custom_settings',
        translate: 'nav.bcm.myCustomizations',
        type: ListItemType.Item,
        url: 'user-settings',
        notEditable: true
    },
    [BcmNavigationElementKey.CostCenters]: {
        id: 'cost_centers',
        translate: 'nav.bcm.costCenters',
        type: ListItemType.Item,
        url: 'cost-centers',
        permission: BcmUserPermission.COST_CENTERS_READ,
        tenantPermission: BcmTenantPermission.COST_CENTERS
    },
    [BcmNavigationElementKey.Departments]: {
        id: 'departments',
        translate: 'nav.bcm.departments',
        type: ListItemType.Item,
        url: 'departments',
        permission: BcmUserPermission.DEPARTMENTS_READ,
        tenantPermission: BcmTenantPermission.DEPARTMENTS,
    },
    [BcmNavigationElementKey.Employees]: {
        id: 'employees',
        translate: 'nav.bcm.employees',
        type: ListItemType.Item,
        url: 'employees',
        permission: BcmUserPermission.EMPLOYEES_READ,
        tenantPermission: BcmTenantPermission.EMPLOYEES,
    },
    [BcmNavigationElementKey.WorkingTimeManagement]: {
        id: 'working_time_management',
        translate: 'nav.bcm.workingTimeManagement',
        type: ListItemType.Item,
        url: 'working-time',
        permission: BcmUserPermission.WORKING_TIME_READ,
        tenantPermission: BcmTenantPermission.WORKING_TIME_MANAGEMENT,
    },
    [BcmNavigationElementKey.Absences]: {
        id: 'absences',
        translate: 'nav.bcm.absences',
        type: ListItemType.Item,
        url: 'absences',
        tenantPermission: BcmTenantPermission.BERTH_ABSENCES
    },
    [BcmNavigationElementKey.Bookings]: {
        id: 'bookings',
        translate: 'nav.bcm.bookings',
        type: ListItemType.Item,
        url: 'bookings',
        permission: BcmUserPermission.BERTHS_READ,
        tenantPermission: BcmTenantPermission.BERTHS_TABLE,
    },
};

export const editableNavigationBcm: BcmNavigationItem[] = [
    navigationElements.dashboard,
    {
        id: 'core_management',
        translate: 'nav.bcm.coreManagement',
        type: ListItemType.Collapsable,
        icon: 'folder_open',
        children: [
            navigationElements.persons,
            navigationElements.companies,
            navigationElements.relations,
            navigationElements.departments,
            navigationElements.employees,
            navigationElements.working_time_management,
            navigationElements.products,
            navigationElements.product_categories,
            navigationElements.cost_centers,
            navigationElements.vouchers
        ]
    },
    {
        id: 'harbour_management',
        translate: 'nav.bcm.harbourManagement',
        type: ListItemType.Collapsable,
        icon: 'directions_boat',
        children: [
            navigationElements.boats,
            navigationElements.piers,
            navigationElements.visual_berth_plan,
            navigationElements.berths_table,
            navigationElements.winter_storages,
            navigationElements.docksite_order,
            navigationElements.electric_meters,
            navigationElements.keys,
            navigationElements.seasons,

            {
                id: 'reports',
                translate: 'nav.bcm.reports',
                type: ListItemType.Collapsable,
                children: [navigationElements.excel_reports, navigationElements.association_reports]
            }
        ]
    },

    {
        id: 'accounting',
        translate: 'nav.bcm.accounting',
        type: ListItemType.Collapsable,
        icon: 'show_chart',
        children: [
            navigationElements.new_invoice,
            navigationElements.invoices,
            navigationElements.quotes,
            navigationElements.invoice_collections,
            {
                id: 'invoice-templates',
                translate: 'nav.bcm.invoiceTemplates',
                type: ListItemType.Collapsable,
                tenantPermission: BcmTenantPermission.INVOICES,
                children: [navigationElements.invoice_default_templates, navigationElements.invoice_wd_templates]
            },
            navigationElements.invoice_settings,
            navigationElements.datev_export,
            navigationElements.sepa,
            navigationElements.pos
        ]
    },

    {
        id: 'documents',
        translate: 'nav.bcm.documentsManagement',
        type: ListItemType.Collapsable,
        icon: 'description',
        tenantPermission: BcmTenantPermission.DOCUMENTS_MANAGEMENT,
        children: [navigationElements.documents_list, navigationElements.document_categories]
    },

    {
        id: 'messages',
        translate: 'nav.bcm.emailMessages',
        type: ListItemType.Collapsable,
        icon: 'messages',
        tenantPermission: BcmTenantPermission.MESSAGES_EMAIL,
        children: [navigationElements.new_message, navigationElements.sent_message]
    },
    {
        id: 'administration',
        translate: 'nav.bcm.administration',
        type: ListItemType.Collapsable,
        icon: 'settings',
        children: [
            navigationElements.users,
            navigationElements.roles_and_permissions,
            navigationElements.import,
            {
                id: 'contract-manager',
                translate: 'nav.bcm.contractManager',
                type: ListItemType.Collapsable,
                icon: 'folder',
                tenantPermission: BcmTenantPermission.CONTRACT_MANAGER,
                children: [
                    navigationElements.contracts,
                    navigationElements.contracts_wd,
                    navigationElements.contract_attachments
                ]
            },
            {
                id: 'email',
                translate: 'nav.bcm.emailConfiguration',
                type: ListItemType.Collapsable,
                icon: 'email',
                children: [
                    navigationElements.email_templates,
                    navigationElements.email_mailings,
                    navigationElements.recipient_lists
                ]
            },
            navigationElements.news
        ]
    }
];

export const adminOnlyNavigationItemsBcm: BcmNavigationItem[] = [navigationElements.settings];

export const userNavigationItemsBcm: BcmNavigationItem[] = [navigationElements.user_custom_settings];

export const editableNavigationBcmTemplate: () => BcmNavigationItem[] = () =>
    JSON.parse(JSON.stringify(editableNavigationBcm));
